import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  InputAdornment,
  Chip,
  Autocomplete,
  Stack,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PageHeader from "../../common/PageHeader";
import useProductCategories from "../../visitor/registration/hooks/useProductCategories";
import useApiEp from "../../../../hooks/useApiEp";

const ExhibitorFilterBar = ({ onSearch, onFilter }) => {
  const { data: Halls } = useApiEp(`/user/get-halls-list-1`);
  const { data: countryList } = useApiEp(`/user/get-exhibitor-country-list`);
  const { data: Mdata } = useProductCategories();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedHall, setSelectedHall] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Match the category level used by the exhibitor API for this event.
  const uniqueCategories = useMemo(() => {
    if (!Mdata?.categories) return [];
    const categoryKey =
      Mdata.type === "triple"
        ? "SubCategory"
        : Mdata.type === "double"
        ? "Category"
        : "MainCategory";
    return [
      ...new Set(
        Mdata.categories
          .map((item) => item[categoryKey])
          .filter(Boolean)
      ),
    ].sort();
  }, [Mdata]);

  // Trigger Filter Helper
  const triggerFilter = (overrides = {}) => {
    onFilter?.({
      hall: selectedHall,
      country: selectedCountry,
      cat: selectedCategories,
      ...overrides,
    });
  };

  // Handlers
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearch?.(value);
  };

  const handleHallChange = (event) => {
    const value = event.target.value;
    setSelectedHall(value);
    triggerFilter({ hall: value });
  };

  const clearHallFilter = () => {
    setSelectedHall("");
    triggerFilter({ hall: "" });
  };

  const handleCountryChange = (event) => {
    const value = event.target.value;
    setSelectedCountry(value);
    triggerFilter({ country: value });
  };

  const clearCountryFilter = () => {
    setSelectedCountry("");
    triggerFilter({ country: "" });
  };

  const handleCategoriesChange = (event, newValue) => {
    setSelectedCategories(newValue);
    triggerFilter({ cat: newValue });
  };

  return (
    <Box sx={{ mb: 4 }}>
      <PageHeader
        title="Exhibitors"
        subtitle="Discover, filter, and connect with participating event exhibitors"
        icon={StorefrontIcon}
      />

      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid #eef2f6",
          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
          p: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
        >
          {/* Hall Filter (Left) */}
          <FormControl size="medium" variant="outlined" sx={{ minWidth: 140 }}>
            <InputLabel>Hall</InputLabel>
            <Select
              label="Hall"
              value={selectedHall}
              onChange={handleHallChange}
              sx={{ borderRadius: "12px", backgroundColor: "#f8fafc" }}
              endAdornment={
                selectedHall ? (
                  <IconButton
                    size="small"
                    onClick={clearHallFilter}
                    sx={{ mr: 2 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
            >
              {Halls?.map((hall) => (
                <MenuItem key={hall.name} value={hall.name}>
                  {hall.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Country Filter */}
          <FormControl size="medium" variant="outlined" sx={{ minWidth: 160 }}>
            <InputLabel>Country</InputLabel>
            <Select
              label="Country"
              value={selectedCountry}
              onChange={handleCountryChange}
              sx={{ borderRadius: "12px", backgroundColor: "#f8fafc" }}
              endAdornment={
                selectedCountry ? (
                  <IconButton
                    size="small"
                    onClick={clearCountryFilter}
                    sx={{ mr: 2 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
            >
              {countryList?.map((country) => (
                <MenuItem key={country.name} value={country.name}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Dynamic Categories Multiselect */}
          <Autocomplete
            multiple
            size="medium"
            options={uniqueCategories}
            value={selectedCategories}
            onChange={handleCategoriesChange}
            sx={{
              minWidth: 260,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#f8fafc",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categories"
                placeholder="Select..."
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  color="primary"
                  variant="outlined"
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
          />

          {/* Search Field (Right) */}
          <TextField
            fullWidth
            placeholder="Search by name, brand, or keyword..."
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            size="medium"
            sx={{
              flexGrow: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#f8fafc",
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": { borderColor: "#0288d1" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Card>
    </Box>
  );
};

export default ExhibitorFilterBar;
